import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useOrder } from '../../contexts/OrderContext';
import { useLocation } from '../../contexts/LocationContext';
import OrderCard from './OrderCard';
import OrderStats from './OrderStats';
import OrderAnalytics from './OrderAnalytics';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFilter, FiBell, FiTrendingUp, FiBarChart3 } = FiIcons;

const OrderManagement = () => {
  const { orders, getOrdersByStatus, updateOrderStatus } = useOrder();
  const { selectedLocation } = useLocation();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showStats, setShowStats] = useState(true);
  const [activeView, setActiveView] = useState('orders'); // 'orders' or 'analytics'

  const statusOptions = [
    { value: 'all', label: 'All Orders', color: 'bg-warm-brown' },
    { value: 'pending', label: 'Pending', color: 'bg-orange-500' },
    { value: 'preparing', label: 'Preparing', color: 'bg-blue-500' },
    { value: 'ready', label: 'Ready', color: 'bg-green-500' },
    { value: 'completed', label: 'Completed', color: 'bg-gray-500' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500' }
  ];

  // Filter orders by location if one is selected
  const locationFilteredOrders = selectedLocation 
    ? orders.filter(order => order.locationId === selectedLocation.id)
    : orders;

  const filteredOrders = selectedStatus === 'all' 
    ? locationFilteredOrders 
    : locationFilteredOrders.filter(order => order.status === selectedStatus);

  const pendingOrders = getOrdersByStatus('pending');
  const preparingOrders = getOrdersByStatus('preparing');

  if (activeView === 'analytics') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-playfair text-warm-brown mb-2">Order Management</h2>
            <p className="text-warm-brown/70">Track and manage customer orders</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveView('orders')}
              className="flex items-center gap-2 px-4 py-2 border border-warm-brown text-warm-brown rounded-lg hover:bg-warm-brown hover:text-cream transition-colors"
            >
              <SafeIcon icon={FiBell} className="text-sm" />
              Orders
            </button>
            <button
              onClick={() => setActiveView('analytics')}
              className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg"
            >
              <SafeIcon icon={FiBarChart3} className="text-sm" />
              Analytics
            </button>
          </div>
        </div>
        <OrderAnalytics />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Order Management</h2>
          <p className="text-warm-brown/70">
            Track and manage customer orders
            {selectedLocation && ` • ${selectedLocation.name}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(pendingOrders.length > 0 || preparingOrders.length > 0) && (
            <div className="flex items-center gap-2 bg-orange-50 text-orange-700 px-4 py-2 rounded-lg">
              <SafeIcon icon={FiBell} className="text-lg animate-pulse" />
              <span className="font-medium">
                {pendingOrders.length + preparingOrders.length} active orders
              </span>
            </div>
          )}
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center gap-2 px-4 py-2 border border-warm-brown text-warm-brown rounded-lg hover:bg-warm-brown hover:text-cream transition-colors"
          >
            <SafeIcon icon={FiTrendingUp} className="text-sm" />
            {showStats ? 'Hide Stats' : 'Show Stats'}
          </button>
          <button
            onClick={() => setActiveView('analytics')}
            className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
          >
            <SafeIcon icon={FiBarChart3} className="text-sm" />
            Analytics
          </button>
        </div>
      </div>

      {/* Order Statistics */}
      {showStats && <OrderStats />}

      {/* Status Filter */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <SafeIcon icon={FiFilter} className="text-warm-brown" />
          <span className="text-warm-brown font-medium">Filter by Status</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {statusOptions.map(status => {
            const count = status.value === 'all' 
              ? locationFilteredOrders.length 
              : locationFilteredOrders.filter(order => order.status === status.value).length;
            
            return (
              <button
                key={status.value}
                onClick={() => setSelectedStatus(status.value)}
                className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
                  selectedStatus === status.value
                    ? `${status.color} text-white`
                    : 'bg-warm-brown/10 text-warm-brown hover:bg-warm-brown/20'
                }`}
              >
                {status.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <SafeIcon icon={FiBell} className="text-6xl text-warm-brown/30 mx-auto mb-4" />
            <p className="text-warm-brown/70 text-lg">No orders found</p>
            <p className="text-warm-brown/50 text-sm">
              {selectedStatus === 'all'
                ? `Orders will appear here when customers place them${selectedLocation ? ` at ${selectedLocation.name}` : ''}`
                : `No ${selectedStatus} orders at the moment${selectedLocation ? ` at ${selectedLocation.name}` : ''}`
              }
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredOrders
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <OrderCard
                    order={order}
                    onStatusChange={updateOrderStatus}
                  />
                </motion.div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderManagement;